import request from "../../utils/request";
import { hasServerErrors, rethrowServerError } from "./Goals";
import { Result } from "@/models/general";
import {
    MonthlyReflectionInput,
    UserReflectionView,
    ManagerReflectionResponseInput,
    ManagerReflectionResponseView,
    ReflectionContributionStatusView,
    ReflectionPrompt
} from '@/models/preformanceManagement';

// Helper to extract payload from Result<T> wrapper
const extractPayload = <T>(response: any): T => {
    if (!response) {
        throw new Error("No response received");
    }
    
    // If response has payload property (from backend Result<T>)
    if (response.payload !== undefined) {
        return response.payload;
    }
    
    // If it's axios response structure
    if (response.data && response.data.payload !== undefined) {
        return response.data.payload;
    }
    
    // Fallback: return the response as-is
    return response;
};

// API 1: Save or submit reflection
// CO-591: the axios interceptor rejects with the PARSED `Result` body (no `.response`), so the
// old `err.response?.data?.errors` read in the view never saw the server's words and a 400
// (`Invalid prompt ID: 1`) rendered as a saved card. Rethrow the way Goals.ts does — an Error
// whose message IS the refusal text, with `__httpStatus` / `__apiErrors` kept non-enumerably.
export async function saveOrSubmitReflection(promptId: number, input: MonthlyReflectionInput): Promise<UserReflectionView> {
    try {
        const response = await request.post(`Reflection/SaveOrSubmit/${promptId}`, input);
        if (!response.data) {
            throw new Error("No response data received");
        }
        return response.data;
    } catch (error: unknown) {
        if (hasServerErrors(error)) {
            rethrowServerError(error, 'Request failed');
        }
        throw error;
    }
}

// API 2: Get reflection contribution status
export async function getReflectionContributionStatus(): Promise<ReflectionContributionStatusView> {
    const response = await request.get(`Reflection/GetContributionStatus`);
    return extractPayload<ReflectionContributionStatusView>(response.data);
}

// API 3: Get current user's reflections
export async function getMyReflections(): Promise<UserReflectionView[]> {
    const response = await request.get(`Reflection/GetMyReflections`);
    return extractPayload<UserReflectionView[]>(response.data);
}

// API 4: Get reflection prompts
export async function getReflectionPrompts(): Promise<ReflectionPrompt[]> {
    const response = await request.get(`Reflection/GetPrompts`);
    return extractPayload<ReflectionPrompt[]>(response.data);
}

// API 5: Submit manager response
export async function submitManagerResponse(input: ManagerReflectionResponseInput): Promise<ManagerReflectionResponseView> {
    const response = await request.post(`Reflection/SubmitManagerResponse`, input);
    return extractPayload<ManagerReflectionResponseView>(response.data);
}

// API 6: Get manager responses for a specific user
export async function getManagerResponses(targetUserId: string): Promise<ManagerReflectionResponseView[]> {
    const response = await request.get(`Reflection/GetManagerResponses/${targetUserId}`);
    return extractPayload<ManagerReflectionResponseView[]>(response.data);
}

// API 7: Get all reflections for a specific user (for managers)
export async function getAllReflections(targetUserId: string): Promise<UserReflectionView[]> {
    const response = await request.get(`Reflection/GetAllReflections/${targetUserId}`);
    return extractPayload<UserReflectionView[]>(response.data);
}

// API 8: Get reflections by specific month/year
export async function getReflectionsByMonth(targetUserId: string, month: number, year: number): Promise<UserReflectionView[]> {
    const response = await request.get(`Reflection/GetReflectionsByMonth/${targetUserId}/${month}/${year}`);
    return extractPayload<UserReflectionView[]>(response.data);
}

// API 9: Get manager responses for current user
export async function getMyManagerResponses(): Promise<ManagerReflectionResponseView[]> {
    const response = await request.get(`Reflection/GetMyManagerResponses`);
    return extractPayload<ManagerReflectionResponseView[]>(response.data);
}

// API 10: Mark manager response as read
export async function markManagerResponseAsRead(responseId: number): Promise<boolean> {
    const response = await request.post(`Reflection/MarkManagerResponseAsRead/${responseId}`);
    return extractPayload<boolean>(response.data);
}

// API 11: Get team reflections for manager (reflections from supervised team members)
export async function getTeamReflections(): Promise<UserReflectionView[]> {
    const response = await request.get(`Reflection/GetTeamReflections`);
    return extractPayload<UserReflectionView[]>(response.data);
}